import { defineStore } from 'pinia';
import { AxiosError, AxiosResponse } from 'axios';
import {
  AppointmentCategory,
  AppointmentType,
  Branch,
  Schedule,
  Site,
} from 'src/interfaces/services';
import {
  Country,
  GPSurgery,
  Practitioner,
  User,
  VaccineProduct,
} from 'src/interfaces/models';
import MarkdownIt from 'markdown-it';
import { useMeta } from 'quasar';
import { useAuthStore } from 'stores/auth';

export interface ServicesStateInterface {
  items: AppointmentType[];
  appointmentTypes: AppointmentType[];
  categories: AppointmentCategory[];
  gp_surgeries: GPSurgery[];
  sites: Site[];
  vaccine_products: VaccineProduct[];
  countries: Country[];
  practitioners: Practitioner[];
  secretKeyModal: {
    show: boolean;
  };
  releaseNotes: string;
  latestReleaseNoteModal: {
    show: boolean;
    data: {
      title: string;
      content: string;
    };
  };
  guestUser: {
    modal: {
      show: boolean;
      loading: boolean;
      item: User;
    };
  };
  weeklyAvailability: Schedule;
  branches: Branch[];
  selected_branch: Branch | null | undefined;
  branch_services: AppointmentType[];
  qrCodeModal: {
    show: boolean;
    item: {
      url: string;
      size: number;
      alt: string;
      filename: string;
    };
  };
}

export const useServicesStore = defineStore('services', {
  state: (): ServicesStateInterface => ({
    items: Array<AppointmentType>(),
    appointmentTypes: Array<AppointmentType>(),
    categories: Array<AppointmentCategory>(),
    gp_surgeries: Array<GPSurgery>(),
    sites: Array<Site>(),
    vaccine_products: Array<VaccineProduct>(),
    countries: Array<Country>(),
    practitioners: Array<Practitioner>(),
    secretKeyModal: {
      show: false,
    },
    releaseNotes: '',
    latestReleaseNoteModal: {
      show: false,
      data: {
        title: '',
        content: '',
      },
    },
    guestUser: {
      modal: {
        show: false,
        loading: false,
        item: {} as User,
      },
    },
    weeklyAvailability: {} as Schedule,
    branches: [
      {
        name: 'Gompels Pharmacy (D&M)',
        slug: 'gompels-pharmacy',
        image: '/assets/bankstreet-pharmacy.webp',
        address: '1 Bank Street, Melksham, SN12 6LE',
        phone: '+441225702198',
        phoneDisplay: '01225 702198',
        email: 'enquiries@gompels.com',
        hours: {
          Monday: '9:00 AM - 5:30 PM',
          Tuesday: '9:00 AM - 5:30 PM',
          Wednesday: '9:00 AM - 5:30 PM',
          Thursday: '9:00 AM - 5:30 PM',
          Friday: '9:00 AM - 5:30 PM',
          Saturday: '9:00 AM - 4:00 PM',
          Sunday: 'CLOSED',
          'Bank Holidays': 'OPEN',
        },
        bankHolidaysHint: 'Times vary. Please enquire for more details',
        location: { lat: 51.37354048879384, lng: -2.138069012839831 },
      },
      {
        name: 'Gompels Spa Pharmacy',
        slug: 'spa-pharmacy',
        image: '/assets/spa.webp',
        address: 'Spa Medical Centre, Snowberry Lane, Melksham, SN12 6UN',
        phone: '+441225703183',
        phoneDisplay: '01225 703183',
        email: 'enquiries@gompels.com',
        hours: {
          Monday: '8:30 AM - 6:30 PM',
          Tuesday: '8:30 AM - 6:30 PM',
          Wednesday: '8:30 AM - 6:30 PM',
          Thursday: '8:30 AM - 6:30 PM',
          Friday: '8:30 AM - 6:30 PM',
          Saturday: 'CLOSED',
          Sunday: 'CLOSED',
          'Bank Holidays': 'CLOSED',
        },
        bankHolidaysHint: null,
        location: { lat: 51.366975625939325, lng: -2.1262377171942894 },
      },
      {
        name: 'Homeward Pharmacy',
        slug: 'homeward-pharmacy',
        image: '/assets/homeward.webp',
        address: 'PO Box 17531, REDDITCH',
        phone: null,
        phoneDisplay: null,
        email: 'pharmacy.fl377@nhs.net',
        hours: {
          Monday: '9:00 AM - 5:00 PM',
          Tuesday: '9:00 AM - 5:00 PM',
          Wednesday: '9:00 AM - 5:00 PM',
          Thursday: '9:00 AM - 5:00 PM',
          Friday: '9:00 AM - 5:00 PM',
          Saturday: 'CLOSED',
          Sunday: 'CLOSED',
          'Bank Holidays': 'CLOSED',
        },
        bankHolidaysHint:
          'When the pharmacy is closed, if you urgently need medical help or advice but it’s not a life-threatening situation, contact NHS 111, by calling 111.',
        location: null,
      },
      {
        name: 'Kimal Pharmacy',
        slug: 'kimal-pharmacy',
        image: '/assets/kimal.webp',
        address: null,
        phone: null,
        phoneDisplay: null,
        email: null,
        hours: null,
        bankHolidaysHint: null,
        location: null,
      },
    ],
    selected_branch: null,
    branch_services: [],
    qrCodeModal: {
      show: false,
      item: {
        url: '',
        size: 200,
        alt: 'QR Code',
        filename: 'qrcode.png',
      },
    },
  }),

  getters: {
    getServiceBySlug: (
      state
    ): ((slug: string) => AppointmentType | undefined) => {
      return (slug: string): AppointmentType | undefined =>
        state.items.find((service) => service.slug === slug);
    },
  },

  actions: {
    setAppointmentTypesMeta(): Promise<AxiosResponse> {
      return new Promise<AxiosResponse>(async (resolve, reject) => {
        await this.api
          .get<AppointmentType[]>('public/appointmenttypes/')
          .then((response: AxiosResponse) => {
            const appointmentTypeList = Array.isArray(response.data)
              ? response.data
              : [];

            const allDescriptions = appointmentTypeList
              .map((service: AppointmentType) => service.description)
              .join(', ');
            const allKeywords = appointmentTypeList
              .map((service: AppointmentType) => service.slug)
              .join(', ');

            useMeta({
              meta: {
                description: { name: 'description', content: allDescriptions },
                keywords: { name: 'keywords', content: allKeywords },
                equiv: {
                  'http-equiv': 'Content-Type',
                  content: 'text/html; charset=UTF-8',
                },
                ogTitle: {
                  property: 'og:title',
                  template(ogTitle) {
                    return `Gompels Pharmacy - ${ogTitle}`;
                  },
                },
              },
            });

            resolve(response);
          })
          .catch((err) => {
            reject(err);
          });
      });
    },

    loadServices(secretKey?: string | null): Promise<AxiosResponse> {
      return new Promise<AxiosResponse>(async (resolve, reject) => {
        if (!import.meta.env.QUASAR_SERVER) {
          secretKey = this.getSecretKey();
        }
        await this.api
          .get<AppointmentType[]>('public/appointmenttypes/', {
            params: { key: secretKey },
          })
          .then((response: AxiosResponse) => {
            this.items = response.data;

            resolve(response);
          })
          .catch((err) => {
            reject(err);
          });
      });
    },

    loadAppointmentCategories(
      categoryId?: string | null,
      secretKey?: string | null,
      payload: object = {}
    ): Promise<AxiosResponse> {
      return new Promise<AxiosResponse>(async (resolve, reject) => {
        if (!import.meta.env.QUASAR_SERVER) {
          secretKey = this.getSecretKey();
        }
        await this.api
          .get<AppointmentCategory | AppointmentCategory[]>(
            `public/appointmentcategories/${
              categoryId ? `${categoryId}/` : ''
            }`,
            {
              params: { key: secretKey, ...payload },
            }
          )
          .then((response: AxiosResponse) => {
            if (categoryId) {
              this.items = response.data.appointment_types;
            } else {
              this.categories = response.data;
            }

            resolve(response);
          })
          .catch((err) => {
            reject(err);
          });
      });
    },

    loadSites(): Promise<AxiosResponse> {
      return new Promise<AxiosResponse>(async (resolve, reject) => {
        await this.api
          .get<Site[]>('public/sites/')
          .then((response: AxiosResponse) => {
            this.sites = response.data.results || response.data;

            resolve(response);
          })
          .catch((err) => {
            reject(err);
          });
      });
    },

    loadVaccineProducts(): Promise<AxiosResponse> {
      return new Promise<AxiosResponse>(async (resolve, reject) => {
        await this.api
          .get<VaccineProduct[]>('private/vaccineproducts/')
          .then((response: AxiosResponse) => {
            this.vaccine_products = response.data.results || response.data;

            resolve(response);
          })
          .catch((err) => {
            reject(err);
          });
      });
    },

    loadPractitioners(): Promise<AxiosResponse> {
      return new Promise<AxiosResponse>(async (resolve, reject) => {
        await this.api
          .get<Practitioner[]>('private/practitioners/')
          .then((response: AxiosResponse) => {
            this.practitioners = response.data.results || response.data;

            resolve(response);
          })
          .catch((err) => {
            reject(err);
          });
      });
    },

    loadCountries(): Promise<AxiosResponse> {
      return new Promise<AxiosResponse>(async (resolve, reject) => {
        await this.api
          .get<Country[]>('private/countries/')
          .then((response: AxiosResponse) => {
            this.countries = response.data.results || response.data;

            resolve(response);
          })
          .catch((err) => {
            reject(err);
          });
      });
    },

    loadGPSurgeries(): Promise<AxiosResponse> {
      return new Promise<AxiosResponse>(async (resolve, reject) => {
        await this.api
          .get<GPSurgery[]>('public/gpsurgeries/')
          .then((response: AxiosResponse) => {
            this.gp_surgeries = response.data;

            resolve(response);
          })
          .catch((err) => {
            reject(err);
          });
      });
    },

    onTabChange(categoryId?: number | null) {
      this.appointmentTypes = categoryId
        ? this.items.filter((item: AppointmentType) => item.id === categoryId)
        : this.items;
    },

    showSecretKeyModal(): void {
      this.secretKeyModal.show = true;
    },

    hideSecretKeyModal(): void {
      this.secretKeyModal.show = false;
    },

    setSecretKey(item: string): void {
      sessionStorage.setItem('secret_key', item);
    },

    removeSecretKey(): void {
      sessionStorage.removeItem('secret_key');
    },

    getSecretKey(): string | null {
      return sessionStorage.getItem('secret_key');
    },

    copyServicesLink(): void {
      const url = new URL(window.location.href);
      const key = this.getSecretKey();
      if (key) {
        url.searchParams.set('key', key.toString());
      } else {
        url.search = '';
      }
      navigator.clipboard.writeText(url.toString());
    },

    async getReleaseNotes(): Promise<void> {
      try {
        const response = await fetch('/CHANGELOG.md');
        this.releaseNotes = this.preprocessReleaseNotes(await response.text());
      } catch (error) {
        console.error('Error loading changelog:', error);
      }
    },

    preprocessReleaseNotes(notes: string): string {
      const lines = notes.split('\n');
      const filteredLines = [];
      let currentVersionLines = [];
      let isRelevantSection = false;

      for (const line of lines) {
        const versionMatch = line.match(
          /(#+) (\[?\d+\.\d+\.\d+\]?\(.*?\)?) \((\d{4})-(\d{2})-\d{2}\)/
        );
        if (versionMatch) {
          if (isRelevantSection) {
            filteredLines.push(...currentVersionLines);
          }
          currentVersionLines = [];
          isRelevantSection = false;
          const p1 = versionMatch[1];
          const p2 = versionMatch[2];
          const p3 = versionMatch[3];
          const p4 = versionMatch[4];
          const version = p2.startsWith('[') ? p2 : `[${p2}]`;
          const date = new Date(`${p3}-${p4}-01`);
          const month = date.toLocaleString('default', { month: 'long' });
          currentVersionLines.push(`${p1} Release ${version} (${month})`);
        } else {
          if (line.trim() && !line.startsWith('#')) {
            isRelevantSection = true;
          }
          if (line.trim().startsWith('*')) {
            const cleanedLine = line.replace(
              /\s*\(\[?[0-9a-f]{7,}\]?.*\)/g,
              ''
            );
            currentVersionLines.push(cleanedLine);
          } else {
            currentVersionLines.push(line);
          }
        }
      }

      if (isRelevantSection) {
        filteredLines.push(...currentVersionLines);
      }

      return filteredLines.join('\n');
    },

    showLatestReleaseNoteModal(): void {
      const md = new MarkdownIt();
      const releaseRegex =
        /## Release \[(\d+\.\d+\.\d+)\]\(.*?\) \((\w+)\)\n([\s\S]*?)(?=## Release \[\d+\.\d+\.\d+\]|\Z)/;
      const match = this.releaseNotes.match(releaseRegex);

      if (match) {
        const title = `[${match[1]}] (${match[2]})`;

        if (this.getReleaseNotesRead(title)) {
          return;
        }

        this.latestReleaseNoteModal.show = true;
        const latestReleaseContent = `## Release [${match[1]}](https://) (${match[2]})\n${match[3]}`;
        this.latestReleaseNoteModal.data.title = title;
        this.latestReleaseNoteModal.data.content =
          md.render(latestReleaseContent);
      } else {
        console.error('No release notes found.');
      }
    },

    hideLatestReleaseNoteModal(): void {
      this.latestReleaseNoteModal.show = false;
    },

    setReleaseNotesRead(releaseTitle: string): void {
      localStorage.setItem(`release_notes_read-${releaseTitle}`, 'true');
    },

    removeReadReleaseNotes(): void {
      for (const key in localStorage) {
        if (key.startsWith('release_notes_read-')) {
          localStorage.removeItem(key);
        }
      }
    },

    getReleaseNotesRead(releaseTitle: string): boolean {
      return (
        localStorage.getItem(`release_notes_read-${releaseTitle}`) === 'true'
      );
    },

    createGuestUser(payload: User): Promise<AxiosResponse> {
      return new Promise<AxiosResponse>(async (resolve, reject) => {
        this.guestUser.modal.loading = true;

        await this.api
          .post<User>('public/users/', payload)
          .then((response: AxiosResponse) => {
            resolve(response);
          })
          .catch((e: AxiosError<string>) => {
            reject(e);
          });

        this.guestUser.modal.loading = false;
      });
    },

    updateGuestUser(payload: User): Promise<AxiosResponse> {
      return new Promise<AxiosResponse>(async (resolve, reject) => {
        this.guestUser.modal.loading = true;

        const $authStore = useAuthStore();
        await this.api
          .put<User>(`public/users/${payload.id}/`, payload, {
            headers: $authStore.getGuestUserHeaders(),
          })
          .then((response: AxiosResponse) => {
            resolve(response);
          })
          .catch((e: AxiosError<string>) => {
            reject(e);
          });

        this.guestUser.modal.loading = false;
      });
    },

    updateGuestUserPartially(
      user_id: number,
      payload: object
    ): Promise<AxiosResponse> {
      return new Promise<AxiosResponse>(async (resolve, reject) => {
        this.guestUser.modal.loading = true;

        const $authStore = useAuthStore();
        await this.api
          .put<User>(`public/users/${user_id}/`, payload, {
            headers: $authStore.getGuestUserHeaders(),
          })
          .then((response: AxiosResponse) => {
            resolve(response);
          })
          .catch((e: AxiosError<string>) => {
            reject(e);
          });

        this.guestUser.modal.loading = false;
      });
    },

    loadGuestUserForm(item: User = {} as User): void {
      this.guestUser.modal.loading = true;
      this.guestUser.modal.item = item;
      this.loadGPSurgeries().then((r) => r);
      this.guestUser.modal.loading = false;
    },

    showGuestUserModal(item: User = {} as User): void {
      this.loadGuestUserForm(item);
      this.guestUser.modal.show = true;
    },

    hideGuestUserModal(): void {
      this.guestUser.modal.show = false;
    },

    fetchAddresses(postcode: string): Promise<AxiosResponse> {
      return new Promise<AxiosResponse>(async (resolve, reject) => {
        await this.api
          .get(`public/addresses/autocomplete/${postcode}/`)
          .then((response: AxiosResponse) => {
            resolve(response);
          })
          .catch((e: AxiosError<string>) => {
            reject(e);
          });
      });
    },

    fetchSelectedAddress(addressId: string): Promise<AxiosResponse> {
      return new Promise<AxiosResponse>(async (resolve, reject) => {
        await this.api
          .get(`public/addresses/${addressId}/`)
          .then((response: AxiosResponse) => {
            resolve(response);
          })
          .catch((e: AxiosError<string>) => {
            reject(e);
          });
      });
    },

    getWeeklyAvailability(
      appointment_type_slug: string
    ): Promise<AxiosResponse> {
      return new Promise<AxiosResponse>(async (resolve, reject) => {
        await this.api
          .get('public/weekly-availability/', {
            params: { appointment_type_slug },
          })
          .then((response: AxiosResponse) => {
            this.weeklyAvailability = response.data;

            resolve(response);
          })
          .catch((e: AxiosError<string>) => {
            reject(e);
          });
      });
    },

    setSelectedBranch(branch: Branch): void {
      this.selected_branch = branch;
    },

    getSelectedBranch(slug: string): void {
      this.selected_branch = this.branches.find(
        (branch) => branch.slug === slug
      );
    },

    getBranchServices(slug: string): Promise<AxiosResponse> {
      return new Promise<AxiosResponse>(async (resolve, reject) => {
        await this.api
          .get<AppointmentType[]>('public/appointmenttypes/', {
            params: { disabled: false, hidden: false },
          })
          .then((response: AxiosResponse) => {
            if (['gompels-pharmacy', 'spa-pharmacy'].includes(slug)) {
              this.branch_services = response.data;
            } else {
              this.branch_services = [];
            }

            resolve(response);
          })
          .catch((err) => {
            reject(err);
          });
      });
    },

    showQrCodeModal(params: {
      url: string;
      size?: number;
      alt?: string;
      filename?: string;
    }): void {
      this.qrCodeModal.item = {
        ...this.qrCodeModal.item,
        ...params,
      };

      this.qrCodeModal.show = true;
    },

    hideQrCodeModal(): void {
      this.qrCodeModal = {
        show: false,
        item: {
          url: '',
          size: 200,
          alt: 'QR Code',
          filename: 'qrcode.png',
        },
      };
    },
  },
});
